/* =============================================================
   ÉVORÈLE v3 — Animation Engine
   Floating hero · Parallax · Scroll-reveal · Stagger
   Magnetic buttons · Product card lift · Button arrows
   Ambient canvas · Counters · Mobile menu · FAQ
   Gallery · Sticky ATC · Newsletter
   ============================================================= */

'use strict';

/* ── Utility ── */
const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const isMobile = () => window.innerWidth < 768;

/* ─────────────────────────────────────
   1. SCROLL-REVEAL (Intersection Observer)
   Staggered fade-up on viewport entry.
───────────────────────────────────── */
(function initReveal() {
  const targets = document.querySelectorAll('.ev-reveal, .ev-reveal-clip');
  if (!targets.length) return;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.08, rootMargin: '0px 0px -40px 0px' });

  targets.forEach(el => observer.observe(el));

  /* Re-run after theme editor section loads */
  document.addEventListener('shopify:section:load', () => {
    document.querySelectorAll('.ev-reveal:not(.is-visible), .ev-reveal-clip:not(.is-visible)')
      .forEach(el => observer.observe(el));
  });
})();

/* ─────────────────────────────────────
   2. HERO CANVAS — Ambient Light Orbs
   Smooth, slow-moving radial gradients
   on the hero background.
───────────────────────────────────── */
(function initHeroCanvas() {
  const canvas = document.getElementById('ev-hero-canvas');
  if (!canvas || prefersReducedMotion) return;

  const ctx = canvas.getContext('2d');
  let W, H, orbs, raf;

  const ORB_CONFIG = [
    { rx: 0.78, ry: 0.18, radius: 320, color: [220, 195, 168], alpha: 0.32, vx:  0.09, vy: 0.06 },
    { rx: 0.12, ry: 0.65, radius: 260, color: [235, 215, 195], alpha: 0.22, vx: -0.07, vy: 0.08 },
    { rx: 0.55, ry: 0.88, radius: 200, color: [245, 230, 210], alpha: 0.18, vx:  0.05, vy:-0.06 },
    { rx: 0.92, ry: 0.75, radius: 180, color: [210, 180, 155], alpha: 0.16, vx: -0.08, vy:-0.04 },
    { rx: 0.38, ry: 0.25, radius: 150, color: [230, 210, 190], alpha: 0.14, vx:  0.06, vy: 0.09 },
  ];

  function resize() {
    W = canvas.offsetWidth;
    H = canvas.offsetHeight;
    canvas.width = W;
    canvas.height = H;
    orbs = ORB_CONFIG.map(o => ({
      ...o,
      x: o.rx * W, y: o.ry * H,
      baseX: o.rx * W, baseY: o.ry * H
    }));
  }

  function draw() {
    ctx.clearRect(0, 0, W, H);
    orbs.forEach(orb => {
      const g = ctx.createRadialGradient(orb.x, orb.y, 0, orb.x, orb.y, orb.radius);
      const [r, g2, b] = orb.color;
      g.addColorStop(0,   `rgba(${r},${g2},${b},${orb.alpha})`);
      g.addColorStop(0.5, `rgba(${r},${g2},${b},${orb.alpha * 0.35})`);
      g.addColorStop(1,   'rgba(0,0,0,0)');
      ctx.beginPath();
      ctx.arc(orb.x, orb.y, orb.radius, 0, Math.PI * 2);
      ctx.fillStyle = g;
      ctx.fill();

      orb.x += orb.vx;
      orb.y += orb.vy;
      if (Math.abs(orb.x - orb.baseX) > 65) orb.vx *= -1;
      if (Math.abs(orb.y - orb.baseY) > 42) orb.vy *= -1;
    });
  }

  function loop() { draw(); raf = requestAnimationFrame(loop); }

  resize();
  loop();

  let rt;
  window.addEventListener('resize', () => { clearTimeout(rt); rt = setTimeout(resize, 200); });
  document.addEventListener('visibilitychange', () => {
    document.hidden ? cancelAnimationFrame(raf) : (raf = requestAnimationFrame(loop));
  });
})();

/* ─────────────────────────────────────
   3. HERO FLOATING IMAGE
   Gentle vertical float on the side
   product image. Pure CSS via class,
   but we also add a subtle rotation.
───────────────────────────────────── */
(function initHeroFloat() {
  if (prefersReducedMotion) return;
  const mediaWrap = document.querySelector('.ev-hero-v2__media');
  if (!mediaWrap) return;

  /* We drive the float via a JS sine wave so hover can pause it cleanly */
  let t = 0;
  let paused = false;
  let raf;

  mediaWrap.addEventListener('mouseenter', () => { paused = true; });
  mediaWrap.addEventListener('mouseleave', () => { paused = false; });

  function floatLoop() {
    if (!paused) {
      t += 0.012; /* speed: smaller = slower */
      const y = Math.sin(t) * 10; /* amplitude: ±10px */
      const rot = Math.sin(t * 0.7) * 0.4; /* ±0.4deg rotation */
      mediaWrap.style.transform = `translateY(${y}px) rotate(${rot}deg)`;
    }
    raf = requestAnimationFrame(floatLoop);
  }
  floatLoop();
})();

/* ─────────────────────────────────────
   4. HERO PARALLAX (scroll)
   Bg layer + side image move at
   different rates for depth.
───────────────────────────────────── */
(function initHeroParallax() {
  if (prefersReducedMotion) return;
  const bgLayer  = document.querySelector('.ev-hero-bg-img');
  const heroText = document.querySelector('.ev-hero-v2__text');
  const heroMedia = document.querySelector('.ev-hero-v2__media');
  if (!bgLayer && !heroText) return;

  let ticking = false;
  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      const y = window.scrollY;
      const vh = window.innerHeight;
      const limit = vh * 1.2;
      if (y < limit) {
        const ratio = y / vh;
        /* Background moves slowest */
        if (bgLayer) bgLayer.style.transform = `translate3d(0, ${y * 0.28}px, 0)`;
        /* Text fades and lifts slightly */
        if (heroText) {
          heroText.style.transform = `translate3d(0, ${y * 0.10}px, 0)`;
          heroText.style.opacity = String(Math.max(0, 1 - ratio * 1.4));
        }
        /* Side image moves faster than bg — more depth */
        if (heroMedia && !isMobile()) {
          heroMedia.style.transform = `translateY(${10 + y * 0.18}px) rotate(${Math.sin(Date.now() * 0.001) * 0.4}deg)`;
        }
      }
      ticking = false;
    });
  }
  window.addEventListener('scroll', onScroll, { passive: true });
})();

/* ─────────────────────────────────────
   5. MAGNETIC BUTTONS
   Subtle cursor-tracking pull effect.
───────────────────────────────────── */
(function initMagneticBtns() {
  if (prefersReducedMotion || isMobile()) return;

  document.querySelectorAll('.ev-btn-magnetic').forEach(btn => {
    btn.addEventListener('mousemove', e => {
      const rect = btn.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = (e.clientX - cx) * 0.22;
      const dy = (e.clientY - cy) * 0.22;
      btn.style.transform = `translate(${dx}px, ${dy}px) translateY(-2px)`;
    });
    btn.addEventListener('mouseleave', () => {
      btn.style.transition = 'transform 0.45s var(--ease-luxury)';
      btn.style.transform = '';
      setTimeout(() => { btn.style.transition = ''; }, 450);
    });
  });
})();

/* ─────────────────────────────────────
   6. PRODUCT CARD — Subtle lift hover
   Removed aggressive tilt. Now does
   a clean vertical lift + shadow.
   Image zooms 3% (set in CSS).
───────────────────────────────────── */
(function initCardHover() {
  if (prefersReducedMotion) return;

  document.querySelectorAll('.ev-product-card').forEach(card => {
    /* On desktop: add very subtle perspective tilt — max 2.5deg */
    if (!isMobile()) {
      card.addEventListener('mousemove', e => {
        const r = card.getBoundingClientRect();
        const x = (e.clientX - r.left) / r.width  - 0.5;
        const y = (e.clientY - r.top)  / r.height - 0.5;
        /* Combine lift + gentle tilt */
        card.style.transform =
          `perspective(900px) rotateY(${x * 2.5}deg) rotateX(${-y * 2}deg) translateY(-5px)`;
      });
      card.addEventListener('mouseleave', () => {
        card.style.transition = 'transform 0.5s var(--ease-luxury), box-shadow 0.5s var(--ease-luxury)';
        card.style.transform = '';
        setTimeout(() => { card.style.transition = ''; }, 500);
      });
    }
  });
})();

/* ─────────────────────────────────────
   7. BUTTON ARROW TRACKING
   Arrow nudges toward cursor direction
   on hover for extra tactile feel.
───────────────────────────────────── */
(function initButtonArrows() {
  if (prefersReducedMotion) return;

  document.querySelectorAll('.ev-btn').forEach(btn => {
    const arrow = btn.querySelector('.ev-btn-arrow');
    if (!arrow) return;
    /* CSS handles the translateX — this adds a micro Y nudge */
    btn.addEventListener('mouseenter', () => {
      arrow.style.transition = 'transform 0.3s var(--ease-spring)';
    });
    btn.addEventListener('mouseleave', () => {
      arrow.style.transform = '';
    });
  });
})();

/* ─────────────────────────────────────
   8. ANIMATED COUNTERS
───────────────────────────────────── */
(function initCounters() {
  const counters = document.querySelectorAll('[data-count]');
  if (!counters.length) return;

  function animateCount(el) {
    const target = parseFloat(el.dataset.count);
    const suffix = el.dataset.suffix || '';
    const prefix = el.dataset.prefix || '';
    const duration = 1800;
    const decimals = el.dataset.decimals ? parseInt(el.dataset.decimals) : 0;
    let start = null;

    function step(ts) {
      if (!start) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 3);
      const current = target * ease;
      el.textContent = prefix + (decimals > 0
        ? current.toFixed(decimals)
        : Math.floor(current)) + suffix;
      if (progress < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }

  const observer = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting && !entry.target.dataset.animated) {
        entry.target.dataset.animated = '1';
        animateCount(entry.target);
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.5 });

  counters.forEach(el => observer.observe(el));
})();

/* ─────────────────────────────────────
   9. STICKY HEADER SHRINK
───────────────────────────────────── */
(function initHeader() {
  const header = document.getElementById('ev-header');
  if (!header) return;
  window.addEventListener('scroll', () => {
    header.classList.toggle('ev-header--scrolled', window.scrollY > 50);
  }, { passive: true });
})();

/* ─────────────────────────────────────
   10. MOBILE MENU
───────────────────────────────────── */
(function initMobileMenu() {
  const toggle   = document.getElementById('ev-menu-toggle');
  const menu     = document.getElementById('ev-mobile-menu');
  const backdrop = document.getElementById('ev-menu-backdrop');
  const close    = document.getElementById('ev-menu-close');
  if (!toggle || !menu) return;

  function open() {
    menu.classList.add('is-open');
    toggle.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    if (backdrop) backdrop.classList.add('is-open');
  }
  function closeMenu() {
    menu.classList.remove('is-open');
    toggle.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    if (backdrop) backdrop.classList.remove('is-open');
  }

  toggle.addEventListener('click', () => menu.classList.contains('is-open') ? closeMenu() : open());
  if (close) close.addEventListener('click', closeMenu);
  if (backdrop) backdrop.addEventListener('click', closeMenu);
  document.addEventListener('keydown', e => { if (e.key === 'Escape') closeMenu(); });
})();

/* ─────────────────────────────────────
   11. FAQ ACCORDION
───────────────────────────────────── */
(function initAccordion() {
  document.querySelectorAll('.ev-accordion-trigger').forEach(btn => {
    btn.addEventListener('click', () => {
      const item  = btn.closest('.ev-accordion-item');
      const panel = item.querySelector('.ev-accordion-panel');
      const isOpen = item.classList.contains('is-open');

      document.querySelectorAll('.ev-accordion-item.is-open').forEach(open => {
        open.classList.remove('is-open');
        open.querySelector('.ev-accordion-panel').style.maxHeight = null;
        open.querySelector('.ev-accordion-trigger').setAttribute('aria-expanded', 'false');
      });

      if (!isOpen) {
        item.classList.add('is-open');
        panel.style.maxHeight = panel.scrollHeight + 'px';
        btn.setAttribute('aria-expanded', 'true');
      }
    });
  });
})();

/* ─────────────────────────────────────
   12. PRODUCT IMAGE GALLERY
───────────────────────────────────── */
(function initGallery() {
  const main   = document.getElementById('ev-gallery-main');
  const thumbs = document.querySelectorAll('.ev-gallery-thumb');
  if (!main || !thumbs.length) return;

  thumbs.forEach(thumb => {
    thumb.addEventListener('click', () => {
      thumbs.forEach(t => t.classList.remove('is-active'));
      thumb.classList.add('is-active');
      main.style.opacity = '0';
      main.style.transform = 'scale(1.015)';
      main.style.transition = 'opacity 0.2s ease, transform 0.2s ease';
      setTimeout(() => {
        main.src    = thumb.dataset.full || thumb.querySelector('img')?.src;
        main.srcset = thumb.dataset.srcset || '';
        main.style.opacity = '1';
        main.style.transform = 'scale(1)';
      }, 180);
    });
  });
})();

/* ─────────────────────────────────────
   13. STICKY ATC (product page)
───────────────────────────────────── */
(function initStickyATC() {
  const bar  = document.getElementById('ev-sticky-atc');
  const main = document.getElementById('ev-main-atc');
  if (!bar || !main) return;
  new IntersectionObserver(([e]) => bar.classList.toggle('is-visible', !e.isIntersecting), { threshold: 0 })
    .observe(main);
})();

/* ─────────────────────────────────────
   14. SMOOTH QUANTITY
───────────────────────────────────── */
document.querySelectorAll('.ev-qty-btn').forEach(btn => {
  btn.addEventListener('click', () => {
    const input = btn.closest('.ev-qty-wrapper')?.querySelector('.ev-qty-input');
    if (!input) return;
    const val = parseInt(input.value, 10) || 1;
    input.value = Math.max(1, val + (btn.dataset.action === 'plus' ? 1 : -1));
  });
});

/* ─────────────────────────────────────
   15. NEWSLETTER FORM
───────────────────────────────────── */
(function initNewsletter() {
  const form = document.getElementById('ev-newsletter-form');
  if (!form) return;
  form.addEventListener('submit', () => {
    const btn = form.querySelector('[type="submit"]');
    if (!btn) return;
    btn.disabled = true;
    btn.textContent = 'Welcome to Évorèle ✦';
    setTimeout(() => { btn.textContent = 'Join the Circle'; btn.disabled = false; }, 4000);
  });
})();

/* ─────────────────────────────────────
   16. RITUAL STEP ACTIVATION (hover)
───────────────────────────────────── */
(function initRitualSteps() {
  const steps = document.querySelectorAll('.ev-ritual-step');
  if (!steps.length) return;
  steps.forEach(step => {
    step.addEventListener('mouseenter', () => {
      steps.forEach(s => s.classList.remove('is-active'));
      step.classList.add('is-active');
    });
  });
})();

/* ─────────────────────────────────────
   17. HORIZONTAL LINE REVEAL
───────────────────────────────────── */
(function initLineReveal() {
  const lines = document.querySelectorAll('.ev-rule');
  const obs = new IntersectionObserver(entries => {
    entries.forEach(e => {
      if (e.isIntersecting) {
        e.target.style.setProperty('--line-scale', '1');
        obs.unobserve(e.target);
      }
    });
  }, { threshold: 0.5 });
  lines.forEach(l => {
    l.style.setProperty('--line-scale', '0');
    l.style.transformOrigin = 'left';
    l.style.transform = 'scaleX(var(--line-scale, 1))';
    l.style.transition = 'transform 0.65s var(--ease-luxury)';
    obs.observe(l);
  });
})();

/* ─────────────────────────────────────
   18. SECTION STAGGER — auto-assign
   delay classes to children of grids
   so they animate in sequence.
───────────────────────────────────── */
(function initStaggerChildren() {
  const grids = document.querySelectorAll(
    '.ev-featured__grid, .ev-ritual__steps, .ev-trust-bar__items'
  );
  grids.forEach(grid => {
    const children = grid.querySelectorAll('.ev-reveal');
    children.forEach((child, i) => {
      /* Stagger: 80ms apart, max 500ms */
      const delay = Math.min(i * 0.08, 0.5);
      child.style.transitionDelay = delay + 's';
    });
  });
})();
